import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, BookOpen, Award, Play, CheckCircle, FileText, Bell, Images, X, ChevronLeft, ChevronRight, GraduationCap, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '../SEO';
import UnavailableCourseModal from '../UnavailableCourseModal';
import LoginModal from '../LoginModal';
import CourseReviewsSection from '../reviews/CourseReviewsSection';
import RatingBadge from '../reviews/RatingBadge';
import { openHotmart } from '../courses/coursesConstants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// IDs dos cursos indisponíveis
const UNAVAILABLE_COURSE_IDS = [24, 25, 26, 27];

const CourseDetailsPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, token } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [offer, setOffer] = useState(null);

  // Disponibilidade real vem da config de checkout (fonte única de verdade).
  const isAvailable = !!offer;
  // Cursos gratuitos (bootcamps/bônus) têm preço zero e não são vendidos.
  const isFree = !!course && Number(course.prices?.ead_recorded || 0) === 0
    && Number(course.prices?.ead_live || 0) === 0
    && Number(course.prices?.presential || 0) === 0;

  useEffect(() => {
    fetchCourse();
    fetchCheckoutConfig();
  }, [courseId]);

  const fetchCheckoutConfig = async () => {
    try {
      const response = await axios.get(`${API}/checkout-config`);
      const modules = response.data?.modules || {};
      setOffer(modules[String(courseId)] || null);
    } catch (error) {
      setOffer(null);
    }
  };

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`);
      setCourse(response.data);
    } catch (error) {
      toast.error('Erro ao carregar curso');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (modality) => {
    // Se o curso não está liberado para venda, mostrar modal
    if (!isAvailable) {
      setShowUnavailableModal(true);
      return;
    }
    
    // Para cursos gratuitos, não precisa de login
    const isFree = course?.prices?.ead_recorded === 0;
    
    try {
      // Buscar URL de checkout da Hotmart/Eduzz
      const response = await axios.get(`${API}/courses/${courseId}/checkout-url?modality=${modality}`);
      const { checkout_url } = response.data;
      
      if (checkout_url) {
        // Redirecionar para a página de checkout da Hotmart/Eduzz
        window.open(checkout_url, '_blank');
        return;
      }
    } catch (error) {

    }
    
    // Fallback: se não encontrar link da Hotmart, usar checkout interno
    if (!isAuthenticated && !isFree) {
      setShowLoginModal(true);
      return;
    }
    navigate(`/checkout/course/${courseId}/${modality}`);
  };

  const modalityLabels = {
    ead_recorded: 'EaD Gravado',
    ead_live: 'EaD Ao Vivo',
    presential: 'Presencial'
  };

  // Aulas de exemplo (mockado - você pode adicionar no banco depois)
  const sampleClasses = [
    { title: 'Introdução ao módulo', duration: '15 min', free: true },
    { title: 'Conceitos fundamentais', duration: '25 min', free: true },
    { title: 'Demonstração prática', duration: '30 min', free: false }
  ];

  // Gallery navigation functions
  const openGallery = (index) => {
    setCurrentImageIndex(index);
    setGalleryOpen(true);
  };

  const closeGallery = () => {
    setGalleryOpen(false);
  };

  const nextImage = () => {
    if (course?.image_gallery) {
      setCurrentImageIndex((prev) => 
        prev === course.image_gallery.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (course?.image_gallery) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? course.image_gallery.length - 1 : prev - 1
      );
    }
  };

  // Keyboard navigation for gallery
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!galleryOpen) return;
      if (e.key === 'Escape') closeGallery();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [galleryOpen, course]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <SEO 
        title={course?.title || 'Detalhes do Curso'}
        description={course?.description || 'Conheça os detalhes deste curso BIM e comece sua jornada de aprendizado.'}
        keywords={`${course?.title || 'curso BIM'}, Revit, Autodesk, formação BIM`}
      />
      <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
        {/* Header */}
        <div className="flex items-center mb-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:text-red-500 mr-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Voltar
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Conteúdo Principal */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero do Curso */}
            <div className="relative h-64 rounded-xl overflow-hidden">
              <img
                src={course.image}
                alt={course.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent flex items-end">
                <div className="p-6 w-full">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge className="bg-red-600 text-white">{course.level}</Badge>
                    <RatingBadge courseId={parseInt(courseId)} />
                  </div>
                  <h1 className="text-3xl font-bold text-white">{course.title}</h1>
                </div>
              </div>
            </div>

            {/* Informações Rápidas */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="pt-6 text-center">
                  <Clock className="text-red-500 mx-auto mb-2" size={32} />
                  <div className="text-2xl font-bold text-white">{course.duration}</div>
                  <div className="text-sm text-gray-400">Carga Horária</div>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="pt-6 text-center">
                  <BookOpen className="text-red-500 mx-auto mb-2" size={32} />
                  <div className="text-2xl font-bold text-white">{course.modules}</div>
                  <div className="text-sm text-gray-400">{course.modules === 1 ? 'Módulo' : 'Módulos'}</div>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="pt-6 text-center">
                  <Award className="text-red-500 mx-auto mb-2" size={32} />
                  <div className="text-2xl font-bold text-white">8.0</div>
                  <div className="text-sm text-gray-400">Nota Mín.</div>
                </CardContent>
              </Card>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="about" className="w-full">
              <TabsList className="bg-zinc-900 w-full">
                <TabsTrigger value="about" className="flex-1 data-[state=active]:bg-red-600">Sobre</TabsTrigger>
                <TabsTrigger value="content" className="flex-1 data-[state=active]:bg-red-600">Conteúdo</TabsTrigger>
                <TabsTrigger value="examples" className="flex-1 data-[state=active]:bg-red-600">Exemplos</TabsTrigger>
                <TabsTrigger value="reviews" className="flex-1 data-[state=active]:bg-red-600">Avaliações</TabsTrigger>
                <TabsTrigger value="certificate" className="flex-1 data-[state=active]:bg-red-600">Certificado</TabsTrigger>
              </TabsList>

              <TabsContent value="about">
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white">Sobre o Curso</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-gray-300 leading-relaxed">{course.description}</p>
                    
                    <div>
                      <h3 className="text-white font-semibold mb-3">O que você vai aprender:</h3>
                      <div className="space-y-2">
                        {course.features.map((feature, index) => (
                          <div key={`feature-${index}-${feature.slice(0, 20)}`} className="flex items-start space-x-2">
                            <CheckCircle size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-300">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="content">
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white">Programa Didático</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {course.syllabus && course.syllabus.length > 0 ? (
                      <div className="space-y-3">
                        {course.syllabus.map((item, index) => (
                          <div key={`syllabus-${index}-${String(item).slice(0, 20)}`} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                                {index + 1}
                              </div>
                              <span className="text-white">{item}</span>
                            </div>
                            <BookOpen className="text-gray-400" size={18} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400">Programa didático será disponibilizado em breve.</p>
                    )}
                    
                    {/* Pré-requisitos */}
                    {course.prerequisites && (
                      <div className="mt-6 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                        <h4 className="text-white font-semibold mb-2 flex items-center">
                          <FileText size={18} className="mr-2 text-red-500" />
                          Pré-requisitos
                        </h4>
                        <p className="text-gray-300">{course.prerequisites}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="examples">
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center">
                      <Images className="mr-2 text-red-500" size={24} />
                      Exemplos de Trabalhos e Projetos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {course.image_gallery && course.image_gallery.length > 0 ? (
                      <div className="space-y-4">
                        <p className="text-gray-400 text-sm mb-4">
                          Veja exemplos de projetos e trabalhos que você poderá desenvolver ao concluir este curso.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {course.image_gallery.map((imageUrl, index) => (
                            <div 
                              key={index}
                              className="relative group cursor-pointer overflow-hidden rounded-lg aspect-video bg-zinc-800"
                              onClick={() => openGallery(index)}
                              data-testid={`gallery-image-${index}`}
                            >
                              <img
                                src={imageUrl}
                                alt={`Exemplo ${index + 1} - ${course.title}`}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                <span className="text-white font-medium">Ampliar</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-gray-500 text-xs mt-4 text-center">
                          Clique nas imagens para ampliar
                        </p>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Images className="mx-auto text-gray-600 mb-4" size={48} />
                        <p className="text-gray-400">Exemplos de projetos serão adicionados em breve.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="reviews">
                <CourseReviewsSection 
                  courseId={parseInt(courseId)} 
                  courseName={course.title}
                />
              </TabsContent>

              <TabsContent value="certificate">
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white">Certificação</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-gradient-to-r from-red-900/20 to-zinc-900 border border-red-500/30 rounded-lg p-6">
                      <div className="flex items-center space-x-4 mb-4">
                        <FileText className="text-red-500" size={48} />
                        <div>
                          <h3 className="text-xl font-bold text-white">Certificado de Conclusão</h3>
                          <p className="text-gray-400">Reconhecido nacionalmente</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <h4 className="text-white font-semibold mb-2">Requisitos para Certificação:</h4>
                        <ul className="space-y-2">
                          <li className="flex items-start space-x-2">
                            <CheckCircle className="text-green-500 mt-0.5" size={18} />
                            <span className="text-gray-300">Estar matriculado no curso (EaD)</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <CheckCircle className="text-green-500 mt-0.5" size={18} />
                            <span className="text-gray-300">Concluir todo o conteúdo do curso</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <CheckCircle className="text-green-500 mt-0.5" size={18} />
                            <span className="text-gray-300"><strong>Nota mínima de 8.0</strong> na prova de avaliação final (10 questões)</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-lg p-4">
                        <h4 className="text-yellow-400 font-semibold mb-2">📝 Sobre a Avaliação</h4>
                        <p className="text-sm text-gray-300 mb-3">
                          A prova é composta por <strong>10 questões de múltipla escolha</strong>, cada uma valendo 1 ponto. 
                          Você precisa acertar pelo menos 7 questões para ser aprovado e receber seu certificado digital.
                        </p>
                        <p className="text-sm text-gray-400">
                          Em caso de reprovação, você verá quais questões errou e poderá refazer a prova.
                        </p>
                      </div>

                      {/* Botão Fazer Prova */}
                      {user && (
                        <div className="pt-4 border-t border-zinc-800">
                          <Button
                            onClick={() => navigate(`/exam/${courseId}`)}
                            className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg"
                            data-testid="start-exam-btn"
                          >
                            <GraduationCap className="mr-2" size={22} />
                            Fazer Prova de Avaliação
                          </Button>
                          <p className="text-center text-sm text-gray-500 mt-2">
                            Teste seus conhecimentos e obtenha seu certificado
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar - Preços */}
          <div>
            <Card className="bg-zinc-900 border-zinc-800 sticky top-24">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  {isFree ? 'Curso Gratuito' : 'Matricule-se'}
                  {isFree ? (
                    <Badge className="bg-green-600/20 text-green-400 border-green-500/30">
                      Grátis
                    </Badge>
                  ) : (!isAvailable && (
                    <Badge className="bg-amber-600/20 text-amber-400 border-amber-500/30">
                      Em Breve
                    </Badge>
                  ))}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isFree ? (
                  <div className="text-center py-4" data-testid="course-free">
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Award className="text-green-400" size={32} />
                    </div>
                    <div className="text-2xl font-bold text-green-400 mb-1">Acesso Gratuito</div>
                    <p className="text-gray-300 mb-4 text-sm">
                      Este curso é gratuito, oferecido como bônus da AJ | BIM. Solicite a liberação do seu acesso e comece agora.
                    </p>
                    <Button
                      onClick={() => window.open(`https://wa.me/5561995792667?text=${encodeURIComponent(`Olá! Gostaria de solicitar o acesso gratuito ao curso "${course.title}".`)}`, '_blank')}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      data-testid="course-free-access-btn"
                    >
                      <CheckCircle className="mr-2" size={18} />
                      Solicitar acesso gratuito
                    </Button>
                  </div>
                ) : isAvailable ? (
                  <div className="text-center py-4" data-testid="course-available">
                    <div className="text-3xl font-bold text-white" data-testid="course-detail-price">
                      {course.prices?.ead_recorded ? `R$ ${Number(course.prices.ead_recorded).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : ''}
                    </div>
                    {course.prices?.ead_recorded > 0 && (
                      <div className="text-xs text-gray-400 mb-3">
                        ou 12x de R$ {(course.prices.ead_recorded / 12).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </div>
                    )}
                    <div className="flex items-center justify-center gap-1.5 text-[11px] text-green-400 bg-green-500/10 border border-green-500/20 rounded-md py-1.5 px-2 mb-3">
                      <Play size={12} className="flex-shrink-0" />
                      <span>Acesso liberado imediatamente após a compra</span>
                    </div>
                    <Button
                      onClick={() => openHotmart(offer)}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      data-testid="course-buy-btn"
                    >
                      <Play className="mr-2" size={18} />
                      Comprar Agora
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4" data-testid="course-sale-soon">
                    <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Clock className="text-amber-400" size={32} />
                    </div>
                    <Badge className="bg-amber-500 text-black font-bold mb-3">DISPONÍVEL PARA VENDA EM BREVE</Badge>
                    <p className="text-gray-300 mb-4 text-sm">
                      Este módulo ainda não está liberado para venda. Cadastre-se para ser avisado(a) assim que as matrículas abrirem.
                    </p>
                    <Button
                      onClick={() => setShowUnavailableModal(true)}
                      className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                      data-testid="notify-course-btn"
                    >
                      <Bell className="mr-2" size={18} />
                      Avise-me quando disponível
                    </Button>
                  </div>
                )}

                <div className="border-t border-zinc-700 pt-4 space-y-2 text-sm">
                  {isAuthenticated && (
                    <button
                      onClick={() => window.dispatchEvent(new CustomEvent('open-assistant', {
                        detail: {
                          context: `Módulo "${course.title}". Descrição: ${course.description || ''}. Tópicos: ${(course.syllabus || []).join(', ')}`,
                          starter: `Tenho uma dúvida sobre o módulo "${course.title}".`,
                        },
                      }))}
                      data-testid="course-ask-ai-btn"
                      className="w-full mb-3 flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 text-white rounded-lg py-2.5 font-semibold transition-colors"
                    >
                      <Sparkles size={16} />
                      Tirar dúvida com a IA
                    </button>
                  )}
                  <div className="flex items-center space-x-2 text-gray-400">
                    <CheckCircle size={16} className="text-green-500" />
                    <span>Certificado de conclusão</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-400">
                    <CheckCircle size={16} className="text-green-500" />
                    <span>Suporte do instrutor</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-400">
                    <CheckCircle size={16} className="text-green-500" />
                    <span>Acesso vitalício</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-400">
                    <CheckCircle size={16} className="text-green-500" />
                    <span>Materiais complementares</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Botão Voltar ao Início */}
        <div className="text-center mt-12">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-zinc-700 text-white hover:bg-zinc-800"
          >
            <ArrowLeft className="mr-2" size={18} />
            Voltar ao Início
          </Button>
        </div>
      </div>

      {/* Modal de Curso Indisponível */}
      <UnavailableCourseModal
        isOpen={showUnavailableModal}
        onClose={() => setShowUnavailableModal(false)}
        course={course}
        user={user}
        token={token}
        onLoginClick={() => {
          setShowUnavailableModal(false);
          setShowLoginModal(true);
        }}
      />

      {/* Modal de Login */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />

      {/* Gallery Lightbox Modal */}
      {galleryOpen && course?.image_gallery && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeGallery}
          data-testid="gallery-modal"
        >
          {/* Close button */}
          <button
            onClick={closeGallery}
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors z-50"
            data-testid="gallery-close-btn"
          >
            <X size={32} />
          </button>

          {/* Navigation buttons */}
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 p-3 text-white/70 hover:text-white bg-black/30 rounded-full transition-colors z-50"
            data-testid="gallery-prev-btn"
          >
            <ChevronLeft size={32} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 p-3 text-white/70 hover:text-white bg-black/30 rounded-full transition-colors z-50"
            data-testid="gallery-next-btn"
          >
            <ChevronRight size={32} />
          </button>

          {/* Image container */}
          <div 
            className="max-w-5xl max-h-[85vh] w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={course.image_gallery[currentImageIndex]}
              alt={`Exemplo ${currentImageIndex + 1} - ${course.title}`}
              className="w-full h-full object-contain rounded-lg"
            />
            <div className="text-center mt-4">
              <span className="text-white/70 text-sm">
                {currentImageIndex + 1} / {course.image_gallery.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetailsPage;